import InfoIcon from '@mui/icons-material/Info';
import PickupIcon from '@mui/icons-material/PhoneCallback';
import {
  Alert,
  Box,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';

import { useConnect } from '../ConnectProvider';

// "Which queues are busy — should I jump in?" view. The Streams API only
// exposes the queues in the agent's own routing profile, not live pending
// counts or agents-online totals. Those numbers require a BFF endpoint
// wrapping `connect:GetCurrentMetricData` (tracked in Backlog) — until it
// lands, the panel renders the queue list with a "data pending" notice so
// agents can still see their queue assignments.
//
// Each row carries a "Pick up" button that accepts the currently-routed
// inbound contact. The button isn't actually queue-specific — the AC SDK
// only ever exposes one routable contact per agent — but the per-row layout
// matches what agents expect from the legacy softphone.

export const QueuesPanel: React.FC = () => {
  const { queues, contact, acceptCall } = useConnect();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canAccept =
    !busy &&
    contact !== null &&
    contact.direction === 'inbound' &&
    contact.state !== 'connected' &&
    contact.state !== 'ended';

  const onAccept = async () => {
    setError(null);
    setBusy(true);
    try {
      await acceptCall();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to accept call');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack sx={{ height: '100%' }}>
      <Alert
        severity="info"
        icon={<InfoIcon fontSize="small" />}
        sx={{ borderRadius: 0, py: 0.5, fontSize: 12 }}
      >
        Live pending-call and agents-online counts arrive once the queue-metrics endpoint is wired
        up.
      </Alert>
      {error && (
        <Alert severity="error" sx={{ borderRadius: 0, py: 0.5, fontSize: 12 }}>
          {error}
        </Alert>
      )}
      {queues.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No queues assigned to your routing profile.
          </Typography>
        </Box>
      ) : (
        <List dense sx={{ flex: 1, overflowY: 'auto', py: 0 }}>
          {queues.map((q) => (
            <ListItem
              key={q.queueArn}
              divider
              secondaryAction={
                <Tooltip title={canAccept ? `Pick up call from ${q.name}` : 'No call ringing'}>
                  <span>
                    <IconButton
                      onClick={onAccept}
                      disabled={!canAccept}
                      size="small"
                      aria-label={`Pick up call from ${q.name}`}
                      color="success"
                    >
                      <PickupIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              }
            >
              <ListItemText
                primary={
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {q.name}
                  </Typography>
                }
                secondary={
                  <Box component="span" sx={{ display: 'flex', gap: 2, color: 'text.secondary' }}>
                    <Typography component="span" variant="caption">
                      Pending: {q.pendingCount ?? '—'}
                    </Typography>
                    <Typography component="span" variant="caption">
                      Agents online: {q.agentsOnline ?? '—'}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      )}
    </Stack>
  );
};
